import React from 'react';
import { useMusic } from '../contexts/MusicContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Play, Pause, SkipBack, SkipForward, Music } from 'lucide-react';

const PlayerBar = () => {
  const { user } = useAuth();
  const {
    currentTrack,
    isPlaying,
    showPlayer,
    nextUp,
    crossfadeActive,
    pause,
    play,
    nextTrack,
    previousTrack,
    progressMs,
    durationMs,
    playerReady,
  } = useMusic();

  if (!user || !showPlayer) return null;
  if (!currentTrack && !isPlaying) return null;

  const handlePlayPause = () => {
    if (isPlaying) pause();
    else play();
  };

  const formatTime = (ms) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercent = durationMs > 0 ? (progressMs / durationMs) * 100 : 0;

  return (
    <div
      className="player-bar fixed z-40 h-16 sm:h-[72px] px-3 sm:px-4"
      data-testid="player-bar"
    >
      <div className="max-w-7xl mx-auto w-full h-full flex items-center justify-between">
        {/* Track Info */}
        <div className="flex items-center gap-2 sm:gap-4 w-2/5 sm:w-1/3 min-w-0">
          {currentTrack ? (
            <>
              {currentTrack.album_image ? (
                <img src={currentTrack.album_image} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover shadow-lg flex-shrink-0" data-testid="now-playing-artwork" />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Music className="w-5 h-5 text-[#606060]" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-white font-medium truncate text-xs sm:text-sm" data-testid="now-playing-title">{currentTrack.name}</p>
                <p className="text-[#A0A0A0] text-[10px] sm:text-xs truncate" data-testid="now-playing-artist">
                  {Array.isArray(currentTrack.artists) ? currentTrack.artists.join(', ') : ''}
                </p>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <Music className="w-4 h-4 sm:w-5 sm:h-5 text-[#606060]" />
              </div>
              <div className="hidden sm:block">
                <p className="text-[#606060] text-sm">No track playing</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col items-center gap-0.5 sm:gap-1 w-1/5 sm:w-1/3">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="icon" onClick={previousTrack} disabled={!currentTrack} className="hover:bg-white/10 disabled:opacity-30 h-8 w-8 sm:h-9 sm:w-9 hidden sm:flex" data-testid="prev-track-button">
              <SkipBack className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" fill="currentColor" />
            </Button>
            <Button onClick={handlePlayPause} disabled={!playerReady} className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white hover:bg-gray-200 text-black transition-transform hover:scale-105 disabled:opacity-50" data-testid="play-pause-button">
              {isPlaying ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={nextTrack} disabled={!currentTrack} className="hover:bg-white/10 disabled:opacity-30 h-8 w-8 sm:h-9 sm:w-9 hidden sm:flex" data-testid="next-track-button">
              <SkipForward className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" fill="currentColor" />
            </Button>
          </div>
          {currentTrack && (
            <div className="w-full max-w-[200px] sm:max-w-sm flex items-center gap-1 sm:gap-2">
              <span className="text-[9px] sm:text-[10px] text-[#606060] w-6 sm:w-8 text-right">{formatTime(progressMs)}</span>
              <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#FF007F] rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
              <span className="text-[9px] sm:text-[10px] text-[#606060] w-6 sm:w-8">{formatTime(durationMs)}</span>
            </div>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 w-2/5 sm:w-1/3 justify-end">
          <div className="flex items-center gap-1 sm:hidden">
            <Button variant="ghost" size="icon" onClick={previousTrack} disabled={!currentTrack} className="hover:bg-white/10 disabled:opacity-30 h-8 w-8" data-testid="prev-track-button-mobile">
              <SkipBack className="w-3.5 h-3.5 text-white" fill="currentColor" />
            </Button>
            <Button variant="ghost" size="icon" onClick={nextTrack} disabled={!currentTrack} className="hover:bg-white/10 disabled:opacity-30 h-8 w-8" data-testid="next-track-button-mobile">
              <SkipForward className="w-3.5 h-3.5 text-white" fill="currentColor" />
            </Button>
          </div>
          <div className="hidden lg:flex items-center gap-2 min-w-0">
            {crossfadeActive && nextUp ? (
              <div className="flex items-center gap-2 animate-pulse" data-testid="crossfade-indicator">
                <span className="text-[10px] text-[#39FF14] uppercase tracking-wider flex-shrink-0">Crossfading</span>
                <p className="text-xs text-[#39FF14] truncate max-w-[120px]">{nextUp.name}</p>
              </div>
            ) : nextUp ? (
              <div className="flex items-center gap-2" data-testid="up-next-indicator">
                <span className="text-[10px] text-[#606060] uppercase tracking-wider flex-shrink-0">Next</span>
                <p className="text-xs text-[#A0A0A0] truncate max-w-[140px]">{nextUp.name}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerBar;
